import { Box, SxProps } from '@mui/system';
import { GridCellParams, GridColDef, GridColumnHeaderParams } from '@mui/x-data-grid-pro';
import { useIntl } from 'react-intl';

import {
  DatasetManifestTableDetails,
  useGetDatasetDeliveryDetailQuery,
} from '@verantos-dev/data-access-dataset-delivery';
import { stringCellStyles } from '@verantos-dev/shared-styles';

import { useDatasetModalContext } from '../context/DatasetManifestDialogContext';

const headerEntryStyles: SxProps = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  lineHeight: 'normal',
  padding: '16px 24px',
  fontWeight: '500',
  fontSize: '14px',
  color: '#252A36',
};

const RenderHeader = (params: GridColumnHeaderParams<DatasetManifestTableDetails>) => (
  <Box data-testid={`dataset-delivery-header-${params.colDef.field}`} sx={headerEntryStyles}>
    {params.colDef.headerName}
  </Box>
);

export type DatasetDetailDialogProps = {
  delivery_location: string;
  archive_size: string;
  md5sum: string;
  onClose: () => void;
};

export const useViewDatasetDetailModal = () => {
  const intl = useIntl();
  const { itemId, closeDialog } = useDatasetModalContext();
  const datasetinfo = itemId;

  const columns: GridColDef<DatasetManifestTableDetails>[] = [
    {
      headerName: intl.formatMessage({ id: 'dataset-delivery-manifest.tableHeader.file-number' }),
      field: 'file_number',
      type: 'number',
      minWidth: 160,
      maxWidth: 160,
      flex: 0,
      filterable: false,
      sortable: false,
      renderHeader: RenderHeader,
    },
    {
      headerName: intl.formatMessage({ id: 'dataset-delivery-manifest.tableHeader.file-name' }),
      field: 'file_name',
      type: 'string',
      minWidth: 220,
      maxWidth: 300,
      filterable: false,
      sortable: false,
      renderHeader: RenderHeader,
    },
    {
      headerName: intl.formatMessage({
        id: 'dataset-delivery-manifest.tableHeader.file-size-bytes',
      }),
      field: 'file_size_bytes',
      type: 'number',
      minWidth: 220,
      maxWidth: 300,
      filterable: false,
      sortable: false,
      renderHeader: RenderHeader,
      renderCell: (params: GridCellParams<DatasetManifestTableDetails>) => {
        const fileSizeBytes = params?.row.file_size_bytes;
        return (
          <Box
            data-testid={`dataset-delivery-cell-${params.row.file_number}-file-size`}
            sx={stringCellStyles}
          >
            {fileSizeBytes}
          </Box>
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'dataset-delivery-manifest.tableHeader.row-count' }),
      field: 'row_count',
      type: 'number',
      minWidth: 220,
      maxWidth: 300,
      filterable: false,
      sortable: false,
      renderHeader: RenderHeader,
    },
    {
      headerName: intl.formatMessage({ id: 'dataset-delivery-manifest.tableHeader.checksum' }),
      field: 'md5sum',
      type: 'string',
      minWidth: 220,
      maxWidth: 300,
      filterable: false,
      sortable: false,
      renderHeader: RenderHeader,
    },
  ];

  const useDatasetDetailQuery = () => useGetDatasetDeliveryDetailQuery(itemId);

  return {
    columns,
    dialogHeader: intl.formatMessage({
      id: 'dataset-delivery-manifest.title',
      defaultMessage: 'Dataset detail',
    }),
    useDatasetDetailQuery,
    onClose: closeDialog,
    datasetinfo: { datasetinfo },
  };
};
